import { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { Form, Spinner } from "react-bootstrap";
import { BASE_URL, HEADERS } from "../../../utils/constant";
import { toast } from "react-toastify";
import axios from "axios";

function FSRModal({
  show,
  handleClose,
  addFsrToList,
  updateFsrInList,
  fetchFsrs,
  editFSRData,
}) {
  const [fsrName, setFsrName] = useState("");
  const [categoryInputs, setCategoryInputs] = useState([{ id: "", name: "" }]);
  const [removedCategoryIds, setRemovedCategoryIds] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [fsrId, setFsrId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editFSRData) {
      setFsrName(editFSRData.fsrName);
      setCategoryInputs(
        editFSRData.categories.map((cat) => ({
          id: cat.id,
          name: cat.name,
        })),
      );
      setRemovedCategoryIds([]);
      setFsrId(editFSRData.id);
      setIsEditing(true);
    } else {
      resetForm();
      setIsEditing(false);
    }
  }, [editFSRData]);

  const resetForm = () => {
    setFsrName("");
    setCategoryInputs([{ id: "", name: "" }]);
    setRemovedCategoryIds([]);
    setIsEditing(false);
    setFsrId(null);
    setLoading(false);
  };

  const handleModalClose = () => {
    resetForm();
    handleClose();
  };

  const handleAddCategoryInput = () =>
    setCategoryInputs([...categoryInputs, { id: "", name: "" }]);

  const handleChangeCategoryInput = (index, value) => {
    const updatedCategories = [...categoryInputs];
    updatedCategories[index].name = value;
    setCategoryInputs(updatedCategories);
  };

  const handleRemoveCategoryInput = (index) => {
    const removedCategory = categoryInputs[index];
    if (removedCategory && removedCategory.id) {
      setRemovedCategoryIds([
        ...removedCategoryIds,
        { id: removedCategory.id, name: "" },
      ]);
    }

    setCategoryInputs(categoryInputs.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    if (!fsrName.trim()) {
      toast.error("FSR Name is required.");
      return false;
    }

    const hasEmptyCategory = categoryInputs.some(
      (category) => !category.name.trim(),
    );
    if (hasEmptyCategory) {
      toast.error("All categories must have a name.");
      return false;
    }

    return true;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const addedOrUpdatedCategories = categoryInputs.map((category) => {
      return category.id
        ? { id: category.id, name: category.name }
        : { name: category.name };
    });

    const payload = isEditing
      ? {
          id: fsrId,
          fsrName,
          categories: [...addedOrUpdatedCategories, ...removedCategoryIds],
        }
      : {
          fsrName,
          categories: addedOrUpdatedCategories,
        };

    setLoading(true);

    try {
      const response = isEditing
        ? await axios.put(`${BASE_URL}/api/fsrs/${fsrId}/`, payload, {
            headers: HEADERS,
          })
        : await axios.post(`${BASE_URL}/api/fsrs/`, payload, {
            headers: HEADERS,
          });

      if (response.status === (isEditing ? 200 : 201)) {
        toast.success(`FSR ${isEditing ? "updated" : "added"} successfully!`);
        if (isEditing) {
          updateFsrInList(response.data);
        } else {
          addFsrToList(response.data);
        }
        fetchFsrs();
        handleModalClose();
      } else {
        toast.error(
          `Failed to ${isEditing ? "update" : "add"} FSR. Please try again.`,
        );
      }
    } catch (error) {
      toast.error("Error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleModalClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? "Edit FSR" : "Add FSR"}</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleFormSubmit}>
        <Modal.Body>
          <div className="mb-3">
            <Form.Control
              type="text"
              id="fsrName"
              name="fsrName"
              value={fsrName}
              onChange={(e) => setFsrName(e.target.value)}
              placeholder="FSR Name*"
              required
              className="mb-4"
            />
            <h6 className="mb-2">Categories*</h6>
            {categoryInputs.map((category, index) => (
              <div key={index} className="mb-3 d-flex">
                <Form.Control
                  type="text"
                  value={category.name}
                  onChange={(e) =>
                    handleChangeCategoryInput(index, e.target.value)
                  }
                  placeholder="Category Name"
                  className="me-2"
                  required
                />
                {index > 0 && (
                  <Button
                    variant="danger"
                    onClick={() => handleRemoveCategoryInput(index)}
                  >
                    <i className="fas fa-times"></i>
                  </Button>
                )}
              </div>
            ))}
            <Button variant="primary" onClick={handleAddCategoryInput}>
              + Add Category
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Close
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

export default FSRModal;
